<script setup>
/**
 * A step that SAYS something — one box, and one switch for the case where the words have to
 * be exactly yours.
 *
 * ── TWO SHAPES, NEVER BOTH ────────────────────────────────────────────────────────────
 * `types.ts:157` is a union and `schema.ts:134-137` puts `.strict()` on each arm, so a
 * `tell` carries `instruction` OR `exactEn`/`exactAr` and a step carrying both is REFUSED.
 * That makes "which arm" a real setting rather than something to infer from which box
 * happens to be empty — which is why it is a switch the owner throws and not a heuristic.
 *
 * The default arm is the instruction, because it is the one that works in both languages
 * from one box: the assistant phrases the message itself, in whatever language the customer
 * wrote in. The exact-words arm is for a sentence that must not be re-phrased — a price, a
 * policy, a legal line — and it costs an Arabic box, which is why it is not the default.
 *
 * The exact words are ONE `BilingualBox` (P-7.1): typed once, in either language, with the
 * other suggested for a price. A one-language account sees only its language's box.
 *
 * WHAT IT SENDS WITH ITS WORDS (P-4.1, P-4.2): "Send with" Text, a Link button or a Location. A
 * button's link is a fixed https address or an answer a Call above keeps (`pay_url`); a Location is
 * one of the business's places, made in `PlacePicker.vue`. On WhatsApp the button or pin goes under
 * the words; on every other channel the link or the place is written out.
 */
import { computed } from 'vue';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import BilingualBox from './BilingualBox.vue';
import PlacePicker from './PlacePicker.vue';
import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useWasloI18n } from '../../i18n.js';
import {
  isSendableLink,
  MAX_EXACT,
  MAX_INSTRUCTION,
  MAX_LINK_LABEL,
  MAX_LINK_URL,
} from '../../helpers/task-flow-draft.js';
import { nameFor, wasGenerated } from '../../helpers/task-question.js';
import {
  keepsKey,
  keptKeysOf,
  summaryTargetOf,
  withSummaryKept,
} from '../../helpers/task-read-back.js';

const props = defineProps({
  /** A spine row whose step is a `tell`. */
  row: { type: Object, required: true },
  /** The account's "Your customers write in" (P-7.1). */
  languages: { type: String, default: 'both' },
  /** Every row, in order, for the Call a summary would be of. */
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:row']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.MESSAGE.${name}`;

const step = computed(() => props.row.step ?? {});
const exact = computed(() => step.value.exactEn !== undefined);

const write = changes =>
  emit('update:row', { ...props.row, step: { ...step.value, ...changes } });

/** The name follows whichever box is the message, under the same legacy rule as a question. */
const named = (changes, said, previous) =>
  wasGenerated(step.value.name, nameFor(previous))
    ? { ...changes, name: nameFor(said) }
    : changes;

const setInstruction = value =>
  write(named({ instruction: value }, value, step.value.instruction ?? ''));

/**
 * The exact words the step is named after: the English when there is any, else the Arabic.
 * One box means the owner may write in either language only (P-7.1), and a step with no
 * name cannot be saved, so the name has to follow whichever side holds the words.
 */
const exactSaid = body =>
  (body.exactEn ?? '').trim() !== '' ? body.exactEn : (body.exactAr ?? '');

const setExactFields = changes =>
  write(
    named(
      changes,
      exactSaid({ ...step.value, ...changes }),
      exactSaid(step.value)
    )
  );

const setExact = (field, value) => setExactFields({ [field]: value });

/** Words moved between the two fields (`BilingualBox`'s one event for both). */
const setExactPair = pair =>
  setExactFields({ exactEn: pair.en, exactAr: pair.ar });

/**
 * Switching arms rewrites the step rather than adding to it, because the schema refuses a
 * step that carries both. Nothing the owner wrote is thrown away silently: the text moves
 * across, so throwing the switch twice leaves the message where it started.
 */
const setExactMode = on => {
  const body = { ...step.value };
  if (on) {
    const said = body.instruction ?? '';
    delete body.instruction;
    body.exactEn = said;
    body.exactAr = body.exactAr ?? '';
  } else {
    const said = exactSaid(body);
    delete body.exactEn;
    delete body.exactAr;
    body.instruction = said;
  }
  emit('update:row', { ...props.row, step: body });
};

// ── the summary of what was sent (P-10.2) ──────────────────────────────────────────
/** The nearest Call above this message, or `''`: only then is a summary offered. */
const summaryTarget = computed(() =>
  summaryTargetOf(
    props.rows,
    props.rows.findIndex(row => row.step?.id === step.value.id)
  )
);
const summarises = computed(
  () =>
    summaryTarget.value !== '' && step.value.summaryOf === summaryTarget.value
);
const keptKeys = computed(() =>
  summaryTarget.value ? keptKeysOf(props.rows, summaryTarget.value) : []
);
const summaryKeep = computed(() =>
  Array.isArray(step.value.summaryKeep) ? step.value.summaryKeep : []
);

const setSummarises = on =>
  write(
    on
      ? { summaryOf: summaryTarget.value }
      : { summaryOf: undefined, summaryKeep: undefined }
  );

/**
 * Services task review E: an entry may be a bare key or a named `{ key, labelEn, … }` one, set
 * through the connector. Ticking matches either form, and a named entry is never rewritten.
 */
const toggleKept = (name, on) =>
  write({ summaryKeep: withSummaryKept(summaryKeep.value, name, on) });

// ── what it sends with its words (P-4.1, P-4.2) ─────────────────────────────────────
const send = computed(() => step.value.send ?? null);
const sendKind = computed(() => send.value?.kind ?? 'text');
const sendOptions = computed(() =>
  ['text', 'link', 'location'].map(value => ({
    value,
    label: t(key(`SEND_${value.toUpperCase()}`)),
  }))
);

/** A new kind starts EMPTY; the builder's checks then point at what is missing. */
const setSendKind = kind => {
  if (kind === sendKind.value) return;
  if (kind === 'link') {
    write({
      send: { kind: 'link', labelEn: '', labelAr: '', url: { value: '' } },
    });
  } else if (kind === 'location') {
    write({ send: { kind: 'location', placeId: 0 } });
  } else {
    write({ send: undefined });
  }
};
const setLink = changes => write({ send: { ...send.value, ...changes } });

/** The answers a Call above this message keeps: where a link from her system can come from. */
const linkKeys = computed(() => {
  const index = props.rows.findIndex(row => row.step?.id === step.value.id);
  return props.rows
    .slice(0, Math.max(index, 0))
    .filter(row => row.step?.type === 'call')
    .flatMap(row => Object.values(row.step.keep ?? {}));
});
const linkFrom = computed(() =>
  send.value?.url && 'key' in send.value.url ? 'system' : 'fixed'
);
const linkFromOptions = computed(() => [
  { value: 'fixed', label: t(key('LINK_FIXED')) },
  { value: 'system', label: t(key('LINK_SYSTEM')) },
]);
const linkKeyOptions = computed(() =>
  linkKeys.value.map(name => ({ value: name, label: name }))
);
const setLinkFrom = from =>
  setLink({
    url: from === 'system' ? { key: linkKeys.value[0] ?? '' } : { value: '' },
  });
const linkUrlMessage = computed(() => {
  const value = send.value?.url?.value;
  return typeof value === 'string' && value !== '' && !isSendableLink(value)
    ? t(key('LINK_URL_INVALID'))
    : '';
});
const previewLabel = computed(
  () =>
    (send.value?.labelEn ?? '').trim() ||
    (send.value?.labelAr ?? '').trim() ||
    t(key('LINK_LABEL_EMPTY'))
);
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-message-step>
    <div v-if="!exact" class="flex flex-col gap-2">
      <WasloTextArea
        :model-value="step.instruction ?? ''"
        :label="t(key('SAY'))"
        :hint="t(key('SAY_HINT'))"
        :placeholder="t(key('SAY_PLACEHOLDER'))"
        :max-length="MAX_INSTRUCTION"
        min-height="5rem"
        data-waslo-message-say
        @update:model-value="setInstruction"
      />
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('SAY_NOTE')) }}</p>
    </div>

    <div v-else class="flex flex-col gap-3">
      <BilingualBox
        :en="step.exactEn ?? ''"
        :ar="step.exactAr ?? ''"
        :label="t(key('EXACT_LABEL'))"
        :max-length="MAX_EXACT"
        multiline
        kind="task_message"
        :languages="languages"
        data-waslo-message-exact
        @update:en="value => setExact('exactEn', value)"
        @update:ar="value => setExact('exactAr', value)"
        @update:pair="setExactPair"
      />
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('EXACT_NOTE')) }}</p>
    </div>

    <label
      class="flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
      data-waslo-message-exact-toggle
    >
      <Switch
        class="mt-0.5"
        :model-value="exact"
        @update:model-value="setExactMode"
      />
      <span class="flex flex-col min-w-0 gap-0.5">
        <span class="text-sm text-n-slate-12">{{ t(key('EXACT')) }}</span>
        <span class="text-sm text-n-slate-11">{{ t(key('EXACT_ABOUT')) }}</span>
      </span>
    </label>

    <div class="flex flex-col gap-3" data-waslo-message-send>
      <WasloSelect
        :model-value="sendKind"
        :options="sendOptions"
        :label="t(key('SEND_WITH'))"
        :message="t(key('SEND_ABOUT'))"
        data-waslo-message-send-kind
        @update:model-value="setSendKind"
      />

      <template v-if="sendKind === 'link'">
        <div class="grid gap-3 sm:grid-cols-2">
          <WasloInput
            v-if="languages !== 'ar'"
            :model-value="send.labelEn"
            :label="t(key('LINK_LABEL_EN'))"
            :max-length="MAX_LINK_LABEL"
            data-waslo-message-link-label-en
            @update:model-value="value => setLink({ labelEn: value })"
          />
          <WasloInput
            v-if="languages !== 'en'"
            :model-value="send.labelAr"
            :label="t(key('LINK_LABEL_AR'))"
            :max-length="MAX_LINK_LABEL"
            data-waslo-message-link-label-ar
            @update:model-value="value => setLink({ labelAr: value })"
          />
        </div>
        <WasloSelect
          :model-value="linkFrom"
          :options="linkFromOptions"
          :label="t(key('LINK_FROM'))"
          data-waslo-message-link-from
          @update:model-value="setLinkFrom"
        />
        <WasloInput
          v-if="linkFrom === 'fixed'"
          :model-value="send.url?.value ?? ''"
          :label="t(key('LINK_URL'))"
          placeholder="https://"
          :max-length="MAX_LINK_URL"
          :message="linkUrlMessage"
          :message-type="linkUrlMessage ? 'error' : 'info'"
          data-waslo-message-link-url
          @update:model-value="
            value => setLink({ url: { value: value.trim() } })
          "
        />
        <WasloSelect
          v-else
          :model-value="send.url?.key ?? ''"
          :options="linkKeyOptions"
          :label="t(key('LINK_KEY'))"
          :placeholder="t(key('LINK_KEY_PLACEHOLDER'))"
          :message="linkKeys.length === 0 ? t(key('LINK_KEY_NONE')) : ''"
          data-waslo-message-link-key
          @update:model-value="value => setLink({ url: { key: value } })"
        />
        <!-- What the customer sees under the words on WhatsApp. Elsewhere the link is written out. -->
        <div
          class="flex flex-col items-start gap-1"
          data-waslo-message-link-preview
        >
          <span class="text-xs text-n-slate-10">{{ t(key('PREVIEW')) }}</span>
          <span
            class="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1 text-n-blue-11"
          >
            <span class="shrink-0 size-4 i-lucide-external-link" />
            <bdi>{{ previewLabel }}</bdi>
          </span>
        </div>
      </template>

      <PlacePicker
        v-else-if="sendKind === 'location'"
        :model-value="send.placeId"
        @update:model-value="
          id => write({ send: { kind: 'location', placeId: id } })
        "
      />
    </div>

    <div
      v-if="summaryTarget"
      class="flex flex-col gap-3"
      data-waslo-message-summary
    >
      <label
        class="flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
      >
        <Switch
          class="mt-0.5"
          :model-value="summarises"
          data-waslo-message-summary-toggle
          @update:model-value="setSummarises"
        />
        <span class="flex flex-col min-w-0 gap-0.5">
          <span class="text-sm text-n-slate-12">{{ t(key('SUMMARY')) }}</span>
          <span class="text-sm text-n-slate-11">{{
            t(key('SUMMARY_ABOUT'))
          }}</span>
        </span>
      </label>
      <fieldset
        v-if="summarises && keptKeys.length"
        class="flex flex-col gap-2 px-3"
        data-waslo-message-summary-keep
      >
        <legend class="mb-1 text-sm text-n-slate-12">
          {{ t(key('SUMMARY_KEEP')) }}
        </legend>
        <label
          v-for="name in keptKeys"
          :key="name"
          class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
        >
          <Checkbox
            :model-value="keepsKey(summaryKeep, name)"
            :data-waslo-message-summary-kept="name"
            @update:model-value="on => toggleKept(name, on)"
          />
          <bdi>{{ name }}</bdi>
        </label>
      </fieldset>
    </div>
  </div>
</template>
